import React, { useEffect, useState } from 'react';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Clock, 
  Edit2, 
  Save, 
  X, 
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { savePracticeData } from '../../redux/practice/practiceSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Practice } from '../../types';
import { RootState } from '../../redux';
import { normalizePracticeLocations, sanitizePracticeLocation, validateUSPracticeLocation } from '../../utils/practiceLocations';
import { US_STATES_OPTIONS } from '../../utils/usStates';

interface PracticeInfo {
  id: string;
  name: string;
  description: string;
  address: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contact: {
    phone: string;
    email: string;
    website: string;
    fax: string;
  };
  businessHours: {
    monday: { open: string; close: string; closed: boolean };
    tuesday: { open: string; close: string; closed: boolean };
    wednesday: { open: string; close: string; closed: boolean };
    thursday: { open: string; close: string; closed: boolean };
    friday: { open: string; close: string; closed: boolean };
    saturday: { open: string; close: string; closed: boolean };
    sunday: { open: string; close: string; closed: boolean };
  };
  settings: {
    timeZone: string;
    currency: string;
    language: string;
    dateFormat: string;
  };
  branding: {
    logo: string;
    primaryColor: string;
    secondaryColor: string;
  };
}

export const GroupSettings: React.FC = () => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { selectedPractice, isLoading, error } = useSelector((state: RootState) => state.practices);

  // Get practice id from local storage
  const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];

  // Fetch practice data on component mount
  // useEffect(() => {
  //   if (practiceId) {
  //     dispatch(fetchPracticeById(practiceId) as any);
  //   }
  // }, [dispatch, practiceId]);

  // Convert backend practice data to component format
  const convertPracticeToForm = (practice: Practice | null): PracticeInfo => {
    if (!practice) {
      return {
        id: practiceId || '',
        name: '',
        description: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States'
        },
        contact: {
          phone: '',
          email: '',
          website: '',
          fax: ''
        },
        businessHours: {
          monday: { open: "08:00", close: "17:00", closed: false },
          tuesday: { open: "08:00", close: "17:00", closed: false },
          wednesday: { open: "08:00", close: "17:00", closed: false },
          thursday: { open: "08:00", close: "17:00", closed: false },
          friday: { open: "08:00", close: "17:00", closed: false },
          saturday: { open: "09:00", close: "13:00", closed: false },
          sunday: { open: "", close: "", closed: true }
        },
        settings: {
          timeZone: "America/Chicago",
          currency: "USD",
          language: "English",
          dateFormat: "MM/DD/YYYY"
        },
        branding: {
          logo: "",
          primaryColor: "#2563eb",
          secondaryColor: "#64748b"
        }
      };
    }

    const locs = normalizePracticeLocations(practice.locations);
    const first = locs[0];

    return {
      id: practice.id || practiceId || '',
      name: practice.name || '',
      description: practice.DBA || '',
      address: {
        street: first?.address1 || '',
        street2: first?.address2 || '',
        city: first?.city || '',
        state: first?.state || '',
        zipCode: first?.zipCode || '',
        country: 'United States'
      },
      contact: {
        phone: practice.phone || '',
        email: practice.primary_email || '',
        website: practice.website || '',
        fax: ''
      },
      businessHours: {
        monday: { open: "08:00", close: "17:00", closed: false },
        tuesday: { open: "08:00", close: "17:00", closed: false },
        wednesday: { open: "08:00", close: "17:00", closed: false },
        thursday: { open: "08:00", close: "17:00", closed: false },
        friday: { open: "08:00", close: "17:00", closed: false },
        saturday: { open: "09:00", close: "13:00", closed: false },
        sunday: { open: "", close: "", closed: true }
      },
      settings: {
        timeZone: "America/Chicago",
        currency: "USD",
        language: "English",
        dateFormat: "MM/DD/YYYY"
      },
      branding: {
        logo: "",
        primaryColor: "#2563eb",
        secondaryColor: "#64748b"
      }
    };
  };

  // Initialize practice info from backend data
  const [practiceInfo, setPracticeInfo] = useState<PracticeInfo>(() => 
    convertPracticeToForm(selectedPractice)
  );
  const [editForm, setEditForm] = useState<PracticeInfo>(practiceInfo);

  // Update practice info when selectedPractice changes
  useEffect(() => {
    if (selectedPractice) {
      const converted = convertPracticeToForm(selectedPractice);
      setPracticeInfo(converted);
      setEditForm(converted);
    }
  }, [selectedPractice]);

  const handleEdit = () => {
    setEditForm(practiceInfo);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditForm(practiceInfo);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!selectedPractice) return;
    
    setIsSaving(true);
    try {
      const existingLocs = normalizePracticeLocations(selectedPractice.locations);
      const rest = existingLocs.slice(1);

      const primaryCandidate = sanitizePracticeLocation({
        address1: editForm.address.street,
        address2: editForm.address.street2,
        city: editForm.address.city,
        state: editForm.address.state,
        zipCode: editForm.address.zipCode,
      });

      const primaryHasAny =
        primaryCandidate.address1 ||
        (primaryCandidate.address2 || '') ||
        primaryCandidate.city ||
        primaryCandidate.state ||
        primaryCandidate.zipCode;

      let nextLocations = existingLocs;
      if (primaryHasAny) {
        const { valid, errors } = validateUSPracticeLocation(primaryCandidate);
        if (!valid) {
          const msg = Object.values(errors).filter(Boolean).join(' ');
          alert(msg || 'Please enter a valid US address for the primary location.');
          setIsSaving(false);
          return;
        }
        const primary = {
          address1: primaryCandidate.address1,
          ...(primaryCandidate.address2 ? { address2: primaryCandidate.address2 } : {}),
          city: primaryCandidate.city,
          state: primaryCandidate.state,
          zipCode: primaryCandidate.zipCode,
        };
        nextLocations = [primary, ...rest];
      } else {
        nextLocations = rest;
      }

      const updatedPractice = {
        ...selectedPractice,
        name: editForm.name,
        DBA: editForm.description,
        phone: editForm.contact.phone,
        primary_email: editForm.contact.email,
        website: editForm.contact.website,
        locations: nextLocations,
      };

      // Dispatch the save action
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await dispatch(savePracticeData(updatedPractice) as any);
      
      setPracticeInfo(editForm);
      setIsEditing(false);
      setShowSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating practice info:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (path: string, value: string | boolean) => {
    setEditForm(prev => {
      const keys = path.split('.');
      const updated = { ...prev };
      let current: Record<string, unknown> = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] as Record<string, unknown>;
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const formatBusinessHours = (day: string) => {
    const businessHours = practiceInfo.businessHours[day as keyof typeof practiceInfo.businessHours];
    if (businessHours.closed) return 'Closed';
    return `${businessHours.open} - ${businessHours.close}`;
  };

  const timeZones = [
    'America/New_York',
    'America/Chicago', 
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu'
  ];

  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
  const languages = ['English', 'Spanish', 'French', 'German', 'Italian'];
  const dateFormats = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading practice information...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
        <div>
          <p className="text-red-800 font-medium">Error loading practice information</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button 
            onClick={() => {
              // Re-fetch practice data
              if (practiceId) {
                window.location.reload();
              }
            }}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Show message if no practice found
  if (!selectedPractice && !isLoading) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
        <div>
          <p className="text-yellow-800 font-medium">No practice information found</p>
          <p className="text-yellow-600 text-sm mt-1">Please ensure you have a valid practice ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-800">Practice information updated successfully!</span>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-6 h-6 mr-2 text-blue-600" />
            Practice Information
          </h2>
          <p className="text-gray-600 mt-1">Manage your practice's general information and settings</p>
        </div>
        
        {!isEditing ? (
          <button
            onClick={handleEdit}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Information
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Practice Name
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900 font-medium">{practiceInfo.name || 'Not specified'}</p>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description / DBA
            </label>
            {isEditing ? (
              <textarea
                value={editForm.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter practice description or DBA name"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.description || 'No description provided'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2 text-gray-600" />
          Contact Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={editForm.contact.phone}
                onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.contact.phone}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
            {isEditing ? (
              <input
                type="tel"
                value={editForm.contact.fax}
                onChange={(e) => handleInputChange('contact.fax', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.contact.fax}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            {isEditing ? (
              <input
                type="email"
                value={editForm.contact.email}
                onChange={(e) => handleInputChange('contact.email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.contact.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            {isEditing ? (
              <input
                type="url"
                value={editForm.contact.website}
                onChange={(e) => handleInputChange('contact.website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.contact.website}</p>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-gray-600" />
          Address
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address line 1</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Street number and name"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.address.street}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address line 2 <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.address.street2}
                onChange={(e) => handleInputChange('address.street2', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Suite, unit, floor"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.address.street2 || '—'}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.address.city}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            {isEditing ? (
              <select
                value={editForm.address.state}
                onChange={(e) => handleInputChange('address.state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Select state</option>
                {US_STATES_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{practiceInfo.address.state || '—'}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.address.zipCode}
                onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="12345 or 12345-6789"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.address.zipCode}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.address.country}
                onChange={(e) => handleInputChange('address.country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{practiceInfo.address.country}</p>
            )}
          </div>
        </div>
      </div>

      {/* Business Hours */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-gray-600" />
          Business Hours
        </h3>
        
        <div className="space-y-3">
          {Object.entries(practiceInfo.businessHours).map(([day]) => (
            <div key={day} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <span className="font-medium text-gray-900 capitalize w-24">{day}</span>
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={!editForm.businessHours[day as keyof typeof editForm.businessHours].closed}
                      onChange={(e) => handleInputChange(`businessHours.${day}.closed`, !e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500 mr-2"
                    />
                    Open
                  </label>
                  {!editForm.businessHours[day as keyof typeof editForm.businessHours].closed && (
                    <>
                      <input
                        type="time"
                        value={editForm.businessHours[day as keyof typeof editForm.businessHours].open}
                        onChange={(e) => handleInputChange(`businessHours.${day}.open`, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={editForm.businessHours[day as keyof typeof editForm.businessHours].close}
                        onChange={(e) => handleInputChange(`businessHours.${day}.close`, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </>
                  )}
                </div>
              ) : (
                <span className="text-gray-900">{formatBusinessHours(day)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* System Settings */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-gray-600" />
          System Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
            {isEditing ? (
              <select
                value={editForm.settings.timeZone}
                onChange={(e) => handleInputChange('settings.timeZone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {timeZones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{practiceInfo.settings.timeZone}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            {isEditing ? (
              <select
                value={editForm.settings.currency}
                onChange={(e) => handleInputChange('settings.currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {currencies.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{practiceInfo.settings.currency}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            {isEditing ? (
              <select
                value={editForm.settings.language}
                onChange={(e) => handleInputChange('settings.language', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {languages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{practiceInfo.settings.language}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
            {isEditing ? (
              <select
                value={editForm.settings.dateFormat}
                onChange={(e) => handleInputChange('settings.dateFormat', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {dateFormats.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{practiceInfo.settings.dateFormat}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};